import { useMemo } from 'react';
import { useGoodsRoute } from '../../../hooks/useGoodsRoute';

const safeParse = (value: string): Record<string, unknown> | undefined => {
  try {
    return JSON.parse(value) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

/**
 * 상품상세 진입 시 출처 화면(FROM)을 route extras에서 추출한다.
 *
 * 네이티브 상세는 `ProductDetailEventManager`가 FROM을 모든 이벤트에 자동 부착하지만,
 * RN 상세는 그렇지 않으므로 이 값을 `getDefaults`에 넣어 모든 이벤트(VIEW/CLICK_ADD_TO_CART/
 * CLICK_LIKE_GOODS 등)에 전파해야 검색 만족도 등 dbt `prev_screen=$.FROM` 집계가 잡힌다.
 *
 * `moveToGoods`는 FROM을 `extras.logging_analytics` 안에 실으며, 다른 진입 경로가
 * top-level `extras.from`을 쓸 수도 있어 둘 다 확인한다.
 */
export const useGoodsFrom = (): string | undefined => {
  const { params } = useGoodsRoute<'GoodsDetail'>();
  const { extras } = params;

  return useMemo(() => {
    if (extras == null) return undefined;
    const parsed = typeof extras === 'string' ? safeParse(extras) : extras;
    if (parsed == null) return undefined;

    const fromTop = parsed.from;
    if (typeof fromTop === 'string' && fromTop.length > 0) return fromTop;

    const loggingAnalytics = parsed.logging_analytics;
    const loggingParsed =
      typeof loggingAnalytics === 'string'
        ? safeParse(loggingAnalytics)
        : (loggingAnalytics as Record<string, unknown> | undefined);
    const fromLogging = loggingParsed?.FROM;
    return typeof fromLogging === 'string' && fromLogging.length > 0
      ? fromLogging
      : undefined;
  }, [extras]);
};
